import { useLayoutEffect, useRef, type ReactNode } from "react"
import { cn } from "@/lib/cn"
import type { ChartTheme } from "@/lib/chart/theme"

/** Plot-area bounds and the hovered bar's position, in CSS pixels relative to the chart. */
export interface HoverAnchor {
  /** Bar centre. */
  x: number
  /** Bar high and low. */
  top: number
  bottom: number
  /** Half the drawn candle width, so the card clears the candle body. */
  halfWidth: number
  plot: { left: number; right: number; top: number; bottom: number }
}

interface Props {
  theme: ChartTheme
  anchor: HoverAnchor
  /** Full chart width; the card is sized relative to it. */
  chartWidth: number
  title: ReactNode
  badge?: ReactNode
  /** Open / high / low / close, shown as a 2×2 grid. */
  ohlc: Array<{ label: string; value: string; color?: string }>
  /** Secondary rows (indicators, volume, open interest). */
  rows?: Array<{ key: string; label: ReactNode; value: string }>
}

const GAP = 12
const EDGE = 6

/** Card width as a share of the chart, so it reads the same on a phone and a wide monitor. */
export function hoverCardWidth(chartWidth: number): number {
  return Math.round(Math.min(220, Math.max(152, chartWidth * 0.2)))
}

/**
 * Places the card beside the bar: to its right unless that would leave the plot, then to its
 * left; vertically centred on the bar's high–low range and kept inside the plot.
 */
export function placeHoverCard(
  anchor: HoverAnchor,
  size: { width: number; height: number },
): { left: number; top: number; side: "left" | "right" } {
  const { plot } = anchor
  const rightLeft = anchor.x + anchor.halfWidth + GAP
  const fitsRight = rightLeft + size.width <= plot.right - EDGE
  const left = fitsRight
    ? rightLeft
    : Math.max(plot.left + EDGE, anchor.x - anchor.halfWidth - GAP - size.width)
  const centre = (anchor.top + anchor.bottom) / 2
  const top = Math.min(
    Math.max(centre - size.height / 2, plot.top + EDGE),
    Math.max(plot.top + EDGE, plot.bottom - size.height - EDGE),
  )
  return { left, top, side: fitsRight ? "right" : "left" }
}

/** Compact OHLC readout that pops up next to the hovered bar. */
export function OhlcHoverCard({ theme, anchor, chartWidth, title, badge, ohlc, rows = [] }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const width = hoverCardWidth(chartWidth)

  // Measure after render and position directly, so the card never flashes at a stale spot.
  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    const placed = placeHoverCard(anchor, { width: node.offsetWidth, height: node.offsetHeight })
    node.style.left = `${placed.left}px`
    node.style.top = `${placed.top}px`
    node.dataset.side = placed.side
  })

  return (
    <div
      ref={ref}
      role="status"
      aria-live="off"
      className={cn(
        "pointer-events-none absolute z-10 rounded-lg border px-2.5 py-2 font-mono text-[11px] leading-tight shadow-xl backdrop-blur",
        theme.classes.tooltip,
      )}
      style={{ width, left: anchor.x + anchor.halfWidth + GAP, top: anchor.top }}
    >
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <span className={cn("truncate font-sans text-[11px] font-semibold", theme.classes.tooltipStrong)}>{title}</span>
        {badge}
      </div>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
        {ohlc.map((item) => (
          <div key={item.label} className="flex items-baseline justify-between gap-1.5">
            <dt className={theme.classes.hint}>{item.label}</dt>
            <dd className={cn("tabular-nums", theme.classes.tooltipStrong)} style={item.color ? { color: item.color } : undefined}>
              {item.value}
            </dd>
          </div>
        ))}
      </dl>
      {rows.length > 0 && (
        <dl className={cn("mt-1.5 space-y-0.5 border-t pt-1.5 text-[10px]", theme.classes.divider)}>
          {rows.map((row) => (
            <div key={row.key} className="flex items-baseline justify-between gap-2">
              <dt className={cn("inline-flex min-w-0 items-center gap-1 truncate", theme.classes.hint)}>{row.label}</dt>
              <dd className="tabular-nums">{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}
